import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";

import * as THREE from "three";
import * as OBC from "@thatopen/components";
import { CameraTool } from "@treegram/tools-camera";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear } from "@fortawesome/pro-solid-svg-icons";
import { Slider } from "radix-ui";

import { ImgLoader } from "@treegram/loaders-img";
import useProjectContext from "@/services/context/project/use-project-context.ts";
import useProjectFormNavigationContext from "@/pages/takeoff/context/project-form-navigation/use-project-form-navigation-context.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";

import FlowMode from "@/models/enums/flow-mode.ts";
import FileType from "@/models/enums/file-type.ts";
import Card from "@/components/card";
import Button from "@/components/buttons/button";
import useViewerContext from "@/services/context/viewer/instance/use-viewer-context.ts";

const MeasureOverviewToolbar: React.FC = () => {

    const { project } = useProjectContext();
    const { setRoute } = useProjectFormNavigationContext();
    const { projectMeasures, storeysFileData, buildingsFileData } = useProjectMeasureContext();
    const { viewer, getLoaders, getTool } = useViewerContext();

    const [viewMode, setViewMode] = useState<OBC.CameraProjection>("Perspective");
    const [sliderValue, setSliderValue] = useState(0);

    const altimetryMapRef = useRef<Map<ImgLoader, number>>(new Map());
    const sitePositionRef = useRef<THREE.Vector3 | undefined>();

    const planPdfItems = useMemo(
        () => Array.from(storeysFileData.entries()).flatMap(([storeyId, files]) =>
            files.map((file) => ({ storeyId, file }))
        ),
        [storeysFileData],
    );

    const handlePerspectiveChange = useCallback(() => {
        const mode = viewMode === "Perspective" ? "Orthographic" : "Perspective";
        setViewMode(mode);
        getTool(CameraTool)?.setProjection(mode);
    }, [viewMode, getTool]);

    useEffect(() => {
        const firstFile3D = Array.from(buildingsFileData.values())
            .map(files =>
                files.find(f => f.fileType === FileType.File3D && f.sitePosition != null)
            )
            .find(Boolean);

        if (firstFile3D?.sitePosition) {
            sitePositionRef.current = firstFile3D.sitePosition;
        }
    }, [buildingsFileData]);

    useEffect(() => {
        if (viewer == null) return;
        const loaders = getLoaders(ImgLoader);
        if (loaders.length === 0) return;

        loaders.forEach((loaderImg, index) => {
            if (!altimetryMapRef.current.has(loaderImg)) {
                const item = planPdfItems[index];
                if (item) {
                    const storey = projectMeasures.getStorey(item.storeyId);
                    const altimetry = item.file.sitePosition
                        ? (storey?.altimetry ?? 0) - (sitePositionRef.current?.z ?? 0)
                        : storey?.altimetry ?? 0;
                    altimetryMapRef.current.set(loaderImg, altimetry);
                } else {
                    altimetryMapRef.current.set(loaderImg, 0);
                }
            }
        });

        const altimetryToApply = sliderValue * 0.01;
        loaders.forEach((loaderImg) => {
            const defaultAltimetry = altimetryMapRef.current.get(loaderImg) ?? 0;
            loaderImg.updateOptions({ altimetry: defaultAltimetry + altimetryToApply });
        });
    }, [viewer, getLoaders, sliderValue, planPdfItems, projectMeasures]);

    return (
        <Card additionalContentClasses='flex flex-row gap-x-0.75 items-center px-1.5' additionalClasses='h-fit px-1 py-2.25'>

            <Slider.Root
                className='relative flex items-center w-24 touch-none select-none mr-2'
                orientation='horizontal' min={-30} max={30} step={1} defaultValue={[0]}
                value={[sliderValue]}
                onValueChange={(value) => setSliderValue(value[0])}>
                <Slider.Track className='relative grow h-1 rounded bg-gray-100'>
                    <Slider.Range className='absolute h-full rounded-full bg-blue-500' />
                </Slider.Track>
                <Slider.Thumb className='block size-3 rounded-full bg-blue-300 shadow-lg hover:bg-blue-400' />
            </Slider.Root>

            <div className='border-s border-gray-300 h-4'/>
            
            <Button onClick={handlePerspectiveChange} variant='ghost' size='2xs' data-tooltip-id='measure-overview-tooltip' data-tooltip-content="Changer de mode de projection">
                <img src={viewMode === "Perspective" ? "/images/icons/cube-96.png" : "/images/icons/orthogonal-view-96.png"} 
                     alt={viewMode === "Perspective" ? "Cube Icon" : "Orthogonal Icon"} className='size-4.5'/>
            </Button>

            {project.flowMode === FlowMode._3D2D || project.flowMode === FlowMode._2D3DAdvanced && (
                <>
                    <div className='border-s border-gray-300 h-4'/>
                    <Button onClick={() =>setRoute('organisation')} variant='ghost' size='2xs' data-tooltip-id='measure-overview-tooltip' data-tooltip-content="Gérer l'organisation du projet">
                        <FontAwesomeIcon icon={faGear} />
                    </Button>
                </>
            )}

        </Card>
    );
};

export default MeasureOverviewToolbar;